import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../setup'
import {UserModel} from '../auth/models/UserModel'
import {
  createGenericFreshDeskTicket,
  FreshDeskTicketReporterOption,
  getFreshDeskTicketReporterOptions,
} from './api'
import {AssignedReporterSelect} from './AssignedReporterSelect'
import {
  buildAssignedReporterOptions,
  getAssignedReporterEmail,
  getCurrentUserReporterOption,
  isAdministratorUser,
} from './ticketReporterOptions'

type Props = {
  show: boolean
  handleClose: () => void
}

type CreatedTicketView = {
  freshDeskId: string
  subject: string
  body: string
  attachments: Array<{
    name: string
    size: number
  }>
}

const editorToolbarButtonStyle: React.CSSProperties = {
  border: '1px solid #d9e1ec',
  background: '#fff',
  color: '#181C32',
  borderRadius: 6,
  width: 34,
  height: 34,
}

const formatFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`
}

const isBlankHtml = (value: string) => {
  return value
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim().length === 0
}

const SupportTicketModal: React.FC<Props> = ({show, handleClose}) => {
  const currentUser = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel | undefined
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [createSubject, setCreateSubject] = useState('')
  const [createBody, setCreateBody] = useState('')
  const [createFiles, setCreateFiles] = useState<File[]>([])
  const [createLoading, setCreateLoading] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createSuccess, setCreateSuccess] = useState('')
  const [createdTicket, setCreatedTicket] = useState<CreatedTicketView | null>(null)
  const [fileInputKey, setFileInputKey] = useState(0)
  const [reporterOptions, setReporterOptions] = useState<FreshDeskTicketReporterOption[]>([])
  const [reporterOptionsLoading, setReporterOptionsLoading] = useState(false)
  const [selectedReporterEmail, setSelectedReporterEmail] = useState('')
  const isAdminUser = useMemo(() => isAdministratorUser(currentUser), [currentUser])
  const assignedReporterOptions = useMemo(
    () => buildAssignedReporterOptions(reporterOptions, currentUser),
    [currentUser, reporterOptions]
  )

  const resetCreateForm = useCallback(() => {
    setCreateSubject('')
    setCreateBody('')
    setCreateFiles([])
    setCreateError('')
    setCreateSuccess('')
    setCreatedTicket(null)
    setFileInputKey((currentKey) => currentKey + 1)
    setSelectedReporterEmail(getCurrentUserReporterOption(currentUser)?.email ?? '')

    if (editorRef.current) {
      editorRef.current.innerHTML = ''
    }
  }, [currentUser])

  useEffect(() => {
    if (show) {
      resetCreateForm()
    }
  }, [resetCreateForm, show])

  useEffect(() => {
    if (!show || !isAdminUser) {
      setReporterOptions([])
      setReporterOptionsLoading(false)
      return
    }

    let active = true
    setReporterOptionsLoading(true)

    getFreshDeskTicketReporterOptions()
      .then((response) => {
        if (active) {
          setReporterOptions(Array.isArray(response?.data) ? response.data : [])
        }
      })
      .catch(() => {
        if (active) {
          setReporterOptions([])
        }
      })
      .finally(() => {
        if (active) {
          setReporterOptionsLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [isAdminUser, show])

  const applyEditorCommand = (command: string) => {
    editorRef.current?.focus()
    document.execCommand(command, false)
    setCreateBody(editorRef.current?.innerHTML ?? '')
  }

  const closeModal = () => {
    if (!createLoading) {
      handleClose()
    }
  }

  const handleCreateTicket = async () => {
    const assignedReporterEmail = isAdminUser ? getAssignedReporterEmail(selectedReporterEmail, currentUser) : ''
    const selectedReporterOption = assignedReporterOptions.find(
      (option) => option.value.toLowerCase() === assignedReporterEmail.toLowerCase()
    )
    const requesterEmail = assignedReporterEmail || currentUser?.email || currentUser?.username || ''
    const requesterName = assignedReporterEmail
      ? selectedReporterOption?.fullName || requesterEmail
      : `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`.trim() ||
        currentUser?.fullname ||
        requesterEmail
    const normalizedSubject = createSubject.trim()
    const normalizedBody = createBody.trim()

    setCreateError('')
    setCreateSuccess('')

    if (!normalizedSubject) {
      setCreateError('Subject is required.')
      return
    }

    if (!normalizedBody || isBlankHtml(normalizedBody)) {
      setCreateError('Body is required.')
      return
    }

    if (!requesterEmail) {
      setCreateError('Requester email is missing for the current user.')
      return
    }

    setCreateLoading(true)

    try {
      const response = await createGenericFreshDeskTicket({
        subject: normalizedSubject,
        body: normalizedBody,
        requesterEmail,
        requesterName,
        assignedReporterEmail: isAdminUser ? assignedReporterEmail : undefined,
        attachments: createFiles,
      })

      setCreatedTicket({
        freshDeskId: response.data.freshDeskId,
        subject: response.data.subject || normalizedSubject,
        body: normalizedBody,
        attachments: createFiles.map((file) => ({
          name: file.name,
          size: file.size,
        })),
      })
      setCreateSuccess(`FreshDesk ticket #${response.data.freshDeskId} was created.`)
    } catch (error: any) {
      setCreateError(error?.response?.data || 'Unable to create FreshDesk ticket.')
    } finally {
      setCreateLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={closeModal} size='xl'>
      <Modal.Header>
        <Modal.Title>{createdTicket ? 'FreshDesk Ticket Created' : 'Create FreshDesk Ticket'}</Modal.Title>
        <button type='button' className='btn-close' onClick={closeModal} disabled={createLoading}></button>
      </Modal.Header>
      <Modal.Body>
        <div className='mt-2'>
          <div className='alert alert-info py-3'>
            {createdTicket ? 'FreshDesk support ticket details.' : 'Create a FreshDesk support ticket.'}
          </div>
          {createError ? <div className='alert alert-danger py-3'>{createError}</div> : null}
          {createSuccess ? <div className='alert alert-success py-3'>{createSuccess}</div> : null}
          {createdTicket ? (
            <div className='mb-4'>
              <label className='form-label fw-semibold'>Ticket #</label>
              <div className='form-control bg-light' style={{minHeight: 42}}>
                {createdTicket.freshDeskId}
              </div>
            </div>
          ) : null}
          {isAdminUser && !createdTicket ? (
            <div className='mb-4'>
              <label className='form-label fw-semibold'>Assigned Reporter</label>
              <AssignedReporterSelect
                options={assignedReporterOptions}
                value={selectedReporterEmail}
                onChange={setSelectedReporterEmail}
                disabled={createLoading}
                loading={reporterOptionsLoading}
              />
            </div>
          ) : null}
          <div className='mb-4'>
            <label className='form-label fw-semibold'>Subject</label>
            {createdTicket ? (
              <div className='form-control bg-light' style={{minHeight: 42}}>
                {createdTicket.subject || '-'}
              </div>
            ) : (
              <input
                type='text'
                className='form-control'
                value={createSubject}
                onChange={(event) => setCreateSubject(event.target.value)}
                placeholder='Enter ticket subject'
                disabled={createLoading}
              />
            )}
          </div>
          <div className='mb-3'>
            <label className='form-label fw-semibold'>Body</label>
            {createdTicket ? (
              <div
                className='form-control bg-light'
                style={{minHeight: 180, whiteSpace: 'pre-wrap'}}
                dangerouslySetInnerHTML={{__html: createdTicket.body}}
              />
            ) : (
              <>
                <div className='d-flex gap-2 mb-2'>
                  <button type='button' style={editorToolbarButtonStyle} onClick={() => applyEditorCommand('bold')} disabled={createLoading} aria-label='Bold'>
                    <i className='fas fa-bold'></i>
                  </button>
                  <button type='button' style={editorToolbarButtonStyle} onClick={() => applyEditorCommand('italic')} disabled={createLoading} aria-label='Italic'>
                    <i className='fas fa-italic'></i>
                  </button>
                  <button type='button' style={editorToolbarButtonStyle} onClick={() => applyEditorCommand('underline')} disabled={createLoading} aria-label='Underline'>
                    <i className='fas fa-underline'></i>
                  </button>
                  <button type='button' style={editorToolbarButtonStyle} onClick={() => applyEditorCommand('insertUnorderedList')} disabled={createLoading} aria-label='Bulleted list'>
                    <i className='fas fa-list-ul'></i>
                  </button>
                </div>
                <div style={{position: 'relative'}}>
                  {!createBody ? (
                    <div style={{position: 'absolute', top: 14, left: 14, color: '#7E8299', pointerEvents: 'none'}}>
                      Describe the issue...
                    </div>
                  ) : null}
                  <div
                    ref={editorRef}
                    contentEditable={!createLoading}
                    onInput={(event) => setCreateBody((event.target as HTMLDivElement).innerHTML)}
                    className='form-control'
                    style={{minHeight: 180, whiteSpace: 'pre-wrap'}}
                    suppressContentEditableWarning
                  >
                  </div>
                </div>
              </>
            )}
          </div>
          <div className='mb-4'>
            <label className='form-label fw-semibold'>Attachments</label>
            {createdTicket ? (
              createdTicket.attachments.length > 0 ? (
                <div className='d-flex flex-wrap gap-2'>
                  {createdTicket.attachments.map((file) => (
                    <span key={`${file.name}-${file.size}`} className='badge bg-light text-dark border'>
                      {file.name} ({formatFileSize(file.size)})
                    </span>
                  ))}
                </div>
              ) : (
                <div className='form-control bg-light text-muted'>No attachments</div>
              )
            ) : (
              <>
                <input
                  key={fileInputKey}
                  type='file'
                  className='form-control'
                  multiple
                  onChange={(event) => setCreateFiles(Array.from(event.target.files ?? []))}
                  disabled={createLoading}
                />
                {createFiles.length > 0 ? (
                  <div className='d-flex flex-wrap gap-2 mt-2'>
                    {createFiles.map((file) => (
                      <span key={`${file.name}-${file.size}`} className='badge bg-light text-dark border'>
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
          <div className='d-flex gap-2'>
            {createdTicket ? (
              <button type='button' className='btn btn-success' onClick={closeModal} disabled={createLoading}>
                Close
              </button>
            ) : (
              <>
                <button type='button' className='btn btn-light' onClick={closeModal} disabled={createLoading}>
                  Cancel
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  onClick={() => void handleCreateTicket()}
                  disabled={createLoading}
                >
                  {createLoading ? 'Saving...' : 'Create FreshDesk Ticket'}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export {SupportTicketModal}
